import { computed, ref } from "vue";
import { useStore } from "@/core/services/store";
import { setValuesToSave } from "@/composables/useSalesChannelManagementApp";
import Presets from "@/components/Settings/Presets/presets";

const store = useStore();
const integrationsWithPresets = ref([]);

// Each project id adds ~40 chars to the (url encoded) filter query, so keep the
// chunks small enough that the request url stays well below the 8k server limit
const PRESET_CONFIG_CHUNK_SIZE = 20;

export const useIntegration = () => {
  const integrations = computed(() => {
    return store.getters.integrations;
  });
  const selectedIntegration = computed(() => store.getters.selectedIntegration);
  const selectedIntegrationName = computed(() =>
    selectedIntegration.value.name
      .replace(store.getters.selectedApp.name, "")
      .trim()
  );
  const selectedIntegrationLabel = computed(
    () =>
      selectedIntegration.value.baseData?.value?.label ||
      selectedIntegrationName.value
  );
  const selectedIntegrationSaveValues = computed(
    () => store.getters.valuesToSave
  );

  async function getIntegrationsWithPresets() {
    if (integrations.value?.length === 0) {
      integrationsWithPresets.value = [];
    }

    let presets = await getPresetConfigs(
      integrations.value.map(value => value.id)
    );

    const migrationChecks =
      store.getters["variables/configValueByName"]("migrationChecks")?.value;

    integrationsWithPresets.value = integrations.value.map(integration => {
      const baseData = presets.find(
        preset =>
          preset.project_id === integration.id && preset.name === "baseData"
      )?.value;

      return {
        ...integration,
        label: baseData?.label,
        status: store.getters.presetProjects.find(
          project => project.id === integration.id
        )?.status,
        featureStatus: getActiveFeatures(
          presets.find(
            preset =>
              preset.project_id === integration.id && preset.name === "features"
          )?.value,
          presets.filter(
            preset =>
              preset.project_id === integration.id &&
              preset.name.includes("BaseSettings")
          )
        ),
        softMigrated: presets.find(
          preset =>
            preset.name === "migrationType" &&
            preset.project_id === integration.id
        )?.value?.softMigrated,
        productiveMode: baseData?.active ?? false,
        migrationActive:
          presets.find(
            preset =>
              preset.project_id === integration.id &&
              preset.name === "migration"
          )?.value?.migrationActive ||
          migrationChecks?.[integration.id]?.migrationFinished === false,
        salesChannel: presets.find(
          preset =>
            preset.project_id === integration.id &&
            preset.name === "salesChannel"
        ),
        wasAlreadyActivated:
          !!presets.find(
            preset =>
              preset.project_id === integration.id && preset.name === "baseData"
          )?.value?.activatedAt ?? false
      };
    });
  }

  function getActiveFeatures(features = {}, baseSettings) {
    const activeFeatures = Object.keys(features)
      .filter(key => features[key] === true)
      .map(feature =>
        store.getters.availableFeatures.includes(feature) ? feature : null
      )
      .filter(f => f !== null);

    return mapFeaturesToValues(activeFeatures, baseSettings);
  }

  function mapFeaturesToValues(featuresArray, objectsArray) {
    const result = {};

    featuresArray.forEach(feature => {
      const object = objectsArray.find(
        obj => obj.name === `${feature}BaseSettings`
      );
      result[feature] = object ? object.value.active : null;
    });

    return result;
  }

  async function getPresetConfigs(projectIds) {
    if (!projectIds?.length) {
      return [];
    }

    const chunks = [];
    for (let i = 0; i < projectIds.length; i += PRESET_CONFIG_CHUNK_SIZE) {
      chunks.push(projectIds.slice(i, i + PRESET_CONFIG_CHUNK_SIZE));
    }

    const responses = await Promise.all(
      chunks.map(chunk => {
        const presetConfigFilter = [
          {
            key: "project_id",
            op: "in",
            value: chunk
          }
        ];

        if (store.getters.selectedApp.name === "shopify") {
          presetConfigFilter.push({
            key: "name",
            op: "notEquals",
            value: "credentials"
          });
        }

        return Presets.getAll(
          { showAll: true, noPagination: true },
          presetConfigFilter
        );
      })
    );

    return responses.flatMap(presetConfigs => presetConfigs?.data ?? []);
  }

  function formValueChange(value, feature, area = null, features = {}) {
    if (!feature || !value.name) return;

    const payload = {
      feature: feature,
      name: value.name,
      value: value.value,
      area: area,
      features: features
    };

    setValuesToSave(payload);
  }

  return {
    formValueChange,
    integrations,
    integrationsWithPresets,
    selectedIntegration,
    selectedIntegrationName,
    selectedIntegrationLabel,
    selectedIntegrationSaveValues,
    getIntegrationsWithPresets
  };
};
